import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RestApiModel } from '../../../shared/model/rest-api.model';
import { CustomerGroupModel } from '../../customerUser/model/customer-group.model';
import { MetadataModel, SearchSiteTier, UserModel } from '../model/user.model';

@Injectable()
export class UsersService {
  private readonly USERS_ENDPOINT = RestApiModel.USERS_API_URL;
  private readonly SPUSERS_ENDPOINT = `${this.USERS_ENDPOINT}/getSPUser`;
  private readonly CUSTOMERUSERS_ENDPOINT = `${
    this.USERS_ENDPOINT
  }/getCustomerUser`;
  private readonly USERS_GROUPS_ENDPOINT = `${this.USERS_ENDPOINT}/groups`;
  private readonly USERS_EDIT_INFO_ENDPOINT = `${this.USERS_ENDPOINT}/editInfo`;
  private readonly USERS_INFO_ENDPOINT = `${this.USERS_ENDPOINT}/getUserInfo`;
  private readonly USERS_INFO_BY_EMAIL_ENDPOINT = `${
    this.USERS_ENDPOINT
  }/getUserInfoByEmail`;
  private readonly USERS_CREATE_ENDPOINT = `${this.USERS_ENDPOINT}/createUser`;
  private readonly CUSTOMER_URL = RestApiModel.PROXY_API_URL + '/customers';
  private readonly CUSTOMER = RestApiModel.PROXY_API_URL + '/customer';
  private readonly CUSTOMER_SERVICE_URL =
    RestApiModel.PROXY_API_URL + '/customerService';
  private readonly SITES_URL = RestApiModel.PROXY_API_URL + '/sites';
  private readonly METADATA_URL = RestApiModel.PROXY_API_URL + '/user/';
  private readonly USER_PASSWORD_RESET_ENDPOINT = `${
    this.USERS_ENDPOINT
  }/changePasswordUser`;
  private readonly USER_PASSWORD_RESETURL_ENDPOINT = `${
    this.USERS_ENDPOINT
  }/ResetPasswordUrl`;
  private readonly SERVICE_PROVIDER_URL =
    RestApiModel.PROXY_API_URL + '/serviceProviders';
  private readonly SPUSERS_CREATE_ENDPOINT = `${
    this.USERS_ENDPOINT
  }/createSPUser`;
  private readonly SPUSERS_UPDATE_ENDPOINT = `${
    this.USERS_ENDPOINT
  }/updateSPUser`;

  private readonly SCUSERS_UPDATE_ENDPOINT = `${
    this.USERS_ENDPOINT
  }/updateSCUser`;

  private readonly CUSTOMERUSERS_CREATE_ENDPOINT = `${
    this.USERS_ENDPOINT
  }/createCustomerUser`;
  private readonly CUSTOMERUSERS_UPDATE_ENDPOINT = `${
    this.USERS_ENDPOINT
  }/updateCustomerUser`;

  private readonly USERS_UPDATE_ACCOUNT_ENDPOINT = `${
    this.USERS_ENDPOINT
  }/updateUserAccountStatus`;

  private readonly VALIDATE_EMAIL_EXISTS_ENDPOINT = `${
    this.USERS_ENDPOINT
  }/validateEmailExists`;

  private readonly INTERNAL_USERS_USER_TYPE = 1;
  private readonly CUSTOMER_USERS_USER_TYPE = 2;
  private readonly SP_USERS_USER_TYPE = 3;

  // Resolve HTTP using the constructor
  constructor(private http: HttpClient) {}

  /**
   * GET - Gets all users.
   * @returns Observable: <any>
   */
  getUsers(queryString: string, endCursor: string): Observable<any> {
    const request = {
      queryString: queryString.trim(),
      endCursor: endCursor
    };
    return this.http.post(this.USERS_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        return throwError(error);
      })
    );
  }

  getSPUsers(queryString: string, selectedServiceProvider: string) {
    const request = {
      query: queryString.trim(),
      serviceProviderNumber: selectedServiceProvider
        ? selectedServiceProvider.trim().split(',')
        : [],
      userType: this.SP_USERS_USER_TYPE
    };
    return this.http.post(this.SPUSERS_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        return throwError(error);
      })
    );
  }
  getUsersByCustomerUserManger(
    queryString: string,
    selectedCustomerNumber: string
  ) {
    const request = {
      query: queryString.trim(),
      customerNumber: selectedCustomerNumber
        ? selectedCustomerNumber.trim().split(',')
        : [],
      userType: this.CUSTOMER_USERS_USER_TYPE
    };
    return this.http.post(this.CUSTOMERUSERS_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        return throwError(error);
      })
    );
  }

  /**
   * GET - Gets all serviceProviders.
   * @returns Observable: <any>
   */
  getServiceProviders(): Observable<any> {
    return this.http.get(this.SERVICE_PROVIDER_URL).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        return throwError(error);
      })
    );
  }

  /**
   * GET - Get all user groups.
   * @returns Observable: <any>
   */
  getGroups(): Observable<any> {
    return this.http.get(this.USERS_GROUPS_ENDPOINT).pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * POST - Add groups for a user
   * @param groupIds: Array<string>
   * @param userId: string
   */
  addGroups(groupIds: Array<string>, userId: string): Observable<any> {
    const request = {
      groups: groupIds.map(item => {
        return { groupId: item, userId: userId };
      })
    };
    return this.http.post(this.USERS_GROUPS_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * POST - Delete groups for a user
   * @param groupIds: Array<string>
   * @param userId: string
   */
  deleteGroups(groupIds: Array<string>, userId: string): Observable<any> {
    const request = {
      groups: groupIds.map(item => {
        return { groupId: item, userId: userId };
      }),
      remove: true
    };
    return this.http.post(this.USERS_GROUPS_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * Edit User
   * @param user: UserModel
   */
  editUserInfo(user: UserModel): Observable<any> {
    const request = {
      user: {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        status: user.status,
        metadata: this.setupMetadata(user.metadata)
      }
    };
    return this.http.post(this.USERS_EDIT_INFO_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * Get User
   * @param user: UserModel
   */
  getUserInfo(user: UserModel): Observable<any> {
    const request = {
      user: {
        id: user.id
      }
    };
    return this.http.post(this.USERS_INFO_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * Get User
   * @param user: UserModel
   */
  getUserInfoByEmail(email: string): Observable<any> {
    if (!email) {
      return;
    }
    const request = {
      user: {
        email: email
      }
    };
    return this.http.post(this.USERS_INFO_BY_EMAIL_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * Add User
   * @param user: UserModel
   * @param enableExistingRegisteredUser: when true, instructs API to enable an existing registered user instead of failing on duplicate.
   */
  addUser(
    user: UserModel,
    enableExistingRegisteredUser = false
  ): Observable<any> {
    const request = {
      user: {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        metadata: this.setupMetadata(user.metadata),
        enableExistingRegisteredUser: enableExistingRegisteredUser
      }
    };
    return this.http.post(this.USERS_CREATE_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Add SPUser
   * @param user: UserModel
   * @param enableExistingRegisteredUser: when true, instructs API to enable an existing registered user instead of failing on duplicate.
   */
  createServiceProvider(
    user: any,
    enableExistingRegisteredUser = false
  ): Observable<any> {
    const request = {
      user: {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        serviceProviderNumbers: user.serviceProviderNumbers,
        enableExistingRegisteredUser: enableExistingRegisteredUser
      }
    };
    return this.http.post(this.SPUSERS_CREATE_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Update SPUser
   * @param user: UserModel
   */
  updateServiceProvider(user: any): Observable<any> {
    const request = {
      user: {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        serviceProviderNumbers: user.serviceProviderNumbers
      }
    };
    return this.http.post(this.SPUSERS_UPDATE_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * Update SPUser
   * @param user: UserModel
   */
  updateServiceCenter(user: any): Observable<any> {
    const request = {
      user: {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName
      }
    };
    return this.http.post(this.SCUSERS_UPDATE_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * Add Customer User
   * @param user: UserModel
   * @param enableExistingRegisteredUser: when true, instructs API to enable an existing registered user instead of failing on duplicate.
   */
  createCustomerUser(
    user: any,
    enableExistingRegisteredUser = false
  ): Observable<any> {
    const request = {
      user: {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        metadata: {
          customer: {
            customerNumbers: user.customerNumbers,
            siteIds: user.siteIds,
            siteTiers: user.siteTiers
          },
          claims: user.claims
        },
        enableExistingRegisteredUser: enableExistingRegisteredUser
      }
    };
    return this.http.post(this.CUSTOMERUSERS_CREATE_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => throwError(error))
    );
  }

  /**
   * Update Customer User
   * @param user: UserModel
   */
  updateCustomerUser(user: any): Observable<any> {
    const request = {
      user: {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        metadata: {
          customer: {
            customerNumbers: user.customerNumbers,
            siteIds: user.siteIds,
            siteTiers: user.siteTiers
          },
          claims: user.claims
        }
      }
    };
    return this.http.post(this.CUSTOMERUSERS_UPDATE_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * Update User Account Status
   * @param user: UserModel
   */
  updateUserAccountStatus(user: any): Observable<any> {
    const request = {
      user: {
        userId: user.id,
        statusName: user.status
      }
    };
    return this.http.post(this.USERS_UPDATE_ACCOUNT_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * Sets up user metadata for Create/Update(admin path will break without it)
   * @param metadata: MetadataModel
   * @returns {MetadataModel}
   */
  setupMetadata(metadata: MetadataModel): MetadataModel {
    if (metadata && metadata.customer) {
      const hasCustomers = metadata.customer.customerNumbers
        ? metadata.customer.customerNumbers.length !== 0
        : false;
      const hasSiteIds = metadata.customer.siteIds
        ? metadata.customer.siteIds.length !== 0
        : false;
      if (!hasCustomers && !hasSiteIds) {
        metadata.customer = null;
      } else if (!hasSiteIds) {
        delete metadata.customer.siteIds;
      } else if (!hasCustomers) {
        delete metadata.customer.customerNumbers;
      }
    }
    return metadata;
  }
  /**
   * Get Customer list.
   * @returns {Observable<any>}
   */
  getCustomers(): Observable<any> {
    return this.http.get(this.CUSTOMER_URL).pipe(
      map((res: any) => res),
      catchError(error => Observable.of(error))
    );
  }

  /**
   * Get Tiers
   * @returns {Observable<any>}
   */
  getTiers(customerNumber: string, params?: any): Observable<any> {
    let queryString = `/customer/${customerNumber}/tiers`;
    if (params) {
      queryString += `?parentTierId=${params.parentTierId}&parentLevel=${
        params.parentLevel
      }`;
    }
    return this.http.get(this.CUSTOMER_SERVICE_URL + queryString).pipe(
      map((res: any) => res),
      catchError(error => Observable.of(error))
    );
  }

  /**
   * Get Sites By Customer Id
   * @returns {Observable<any>}
   */
  getSitesByCustomerId(customerNumber: string): Observable<any> {
    return this.http.get(this.CUSTOMER + `/${customerNumber}/sites`).pipe(
      map((res: any) => res.results),
      catchError(error => Observable.of(error))
    );
  }

  /**
   * Get Tiers
   * @returns {Observable<any>}
   */
  getSearchSites(customerNumber: string, siteId: string): Observable<any> {
    let queryString = `/customer/${customerNumber}/site/search`;
    queryString += `?siteNumber=${siteId}`;

    return this.http.get(this.CUSTOMER_SERVICE_URL + queryString).pipe(
      map((siteTier: SearchSiteTier[]) => siteTier),
      catchError(error => Observable.of(error))
    );
  }

  /**
   * Get Sites
   * @returns {Observable<any>}
   */
  getSites(customerNumber: string, params: any): Observable<any> {
    let queryString = `/customer/${customerNumber}/tiers/${
      params.parentTierId
    }/sites?parentLevel=${params.parentLevel}`;

    if (params.pageNumber) {
      queryString += `&pageNumber=${params.pageNumber}`;
    }

    if (params.pageSize) {
      queryString += `&pageSize=${params.pageSize}`;
    }

    return this.http.get(this.CUSTOMER_SERVICE_URL + queryString).pipe(
      map((res: any) => res),
      catchError(error => Observable.of(error))
    );
  }

  /**
   * Get all Sites available for selected customers
   * @returns {Observable<any>}
   */
  getAllSitesForCustomers(customerNumbers: string): Observable<any> {
    return this.http
      .get(this.SITES_URL + `?customerNumbers=${customerNumbers}&pageSize=9000`)
      .pipe(
        map((res: any) => res),
        catchError(error => Observable.of(error))
      );
  }

  /**
   * POST - Send Password Reset email for a user
   * @param id: string
   * @param email: string
   * @returns {Observable<any>}
   */
  changePasswordUser(id: string, email: string): Observable<any> {
    const request = {
      id,
      email
    };

    return this.http.post(this.USER_PASSWORD_RESET_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError(error => Observable.of(error))
    );
  }

  /**
   * POST - generate  Reset password url for a user
   * @param id: string
   * @returns {Observable<any>}
   */
  getResetPasswordUrl(id: string): Observable<any> {
    const request = {
      id
    };

    return this.http.post(this.USER_PASSWORD_RESETURL_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError(error => Observable.of(error))
    );
  }
  /**
   * Validates the Email exists in Auth0
   * @param email: string
   */
  validateEmailExists(emailId: string): Observable<any> {
    const request = {
      email: emailId
    };
    return this.http.post(this.VALIDATE_EMAIL_EXISTS_ENDPOINT, request).pipe(
      map((res: any) => res),
      catchError((error: any) => error)
    );
  }

  /**
   * GET - Gets all getServiceProvidersWithPaging.
   * @returns Observable: <any>
   */
  getServiceProvidersWithPaging(
    pageNumber,
    pageSize,
    searchQuery
  ): Observable<any> {
    return this.http
      .get(
        this.SERVICE_PROVIDER_URL +
          '?pageNumber=' +
          pageNumber +
          '&pageSize=' +
          pageSize +
          '&searchQuery=' +
          searchQuery
      )
      .pipe(
        map((res: any) => res),
        catchError((error: any) => {
          return throwError(error);
        })
      );
  }

  /**
   * GET - Gets all getServiceProvidersAll.
   * @returns Observable: <any>
   */
  getServiceProvidersAll(): Observable<any> {
    return this.http.get(this.SERVICE_PROVIDER_URL).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        return throwError(error);
      })
    );
  }

  /**
   * Get all customer group customers available for selected customers
   * @returns {Observable<any>}
   */
  getCustomerGroupCustomers(
    customerNumber: string
  ): Observable<Array<CustomerGroupModel>> {
    let queryString = `/customer/${customerNumber}/group`;

    return this.http.get(this.CUSTOMER_SERVICE_URL + queryString).pipe(
      map((res: any) => res),
      catchError(error => Observable.of(error))
    );
  }
}
